import { useMemo, useState } from "react";
import { Loader2, Radio } from "lucide-react";
import { PageHero } from "@/components/layout/PageHero";
import { PLANS, STATUSES, SubscriberCard } from "@/components/subscriptions/SubscriberCard";
import { MrrCard, SubsSummary } from "@/components/subscriptions/SubsSummary";
import { useSubscriptions } from "@/lib/subscriptions";
import { useI18n } from "@/i18n/I18nContext";
import { FeesSection } from "@/components/fees/FeesSection";
import { FeeModelCard } from "@/components/fees/FeeModelCard";
import { FxCard } from "@/components/admin/FxCard";
import { RevenueSources } from "@/components/subscriptions/RevenueSources";
import { CartSplitCard } from "@/components/subscriptions/CartSplitCard";
import { RobotCard } from "@/components/subscriptions/RobotCard";
import { ChangesCard } from "@/components/subscriptions/ChangesCard";
import { ProfitCard, useProfit } from "@/components/admin/Profit";
import { MinisiteKeysCard } from "@/components/admin/MinisiteKeysCard";
import { TrialSettingsCard } from "@/components/admin/TrialTools";
import { PayGateCard } from "@/components/admin/PayGateCard";

const SORTS = {
  name: (a, b) => a.name.localeCompare(b.name),
  price: (a, b) => (b.price_ht ?? -1) - (a.price_ht ?? -1),
  renewal: (a, b) => (a.next_renewal || a.ends_at || "9").localeCompare(b.next_renewal || b.ends_at || "9"),
  apps: (a, b) => b.apps_used - a.apps_used,
};

const Picker = ({ label, value, onChange, options, testId }) => (
  <label className="flex min-w-0 flex-col gap-1 text-xs font-semibold uppercase tracking-wider text-neutral-400">
    <span className="break-words">{label}</span>
    <select value={value} onChange={(e) => onChange(e.target.value)} className="w-full min-w-0 rounded-xl border border-neutral-600 bg-[#0d0b0c] px-3 py-2 text-sm normal-case tracking-normal text-white focus:border-[var(--gold)] focus:outline-none" data-testid={testId}>
      {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
    </select>
  </label>
);

const Subscribers = ({ rows, profit }) => {
  const { t } = useI18n();
  const [status, setStatus] = useState("all");
  const [plan, setPlan] = useState("all");
  const [sort, setSort] = useState("name");
  const list = useMemo(() => rows.filter((r) => (status === "all" || r.status === status) && (plan === "all" || r.plan === plan)).sort(SORTS[sort]), [rows, status, plan, sort]);
  return (
    <section className="min-w-0" data-testid="subs-list-section">
      <h2 className="break-words font-display text-base uppercase tracking-wider text-[var(--gold-bright)] md:text-lg">{t("subs.subscribers")} ({list.length})</h2>
      <div className="mt-3 grid min-w-0 grid-cols-1 gap-3 sm:grid-cols-3">
        <Picker label={t("subs.filterStatus")} value={status} onChange={setStatus} testId="subs-filter-status" options={[["all", t("subs.all")], ...STATUSES.map((s) => [s, t(`subs.st.${s}`)])]} />
        <Picker label={t("subs.filterPlan")} value={plan} onChange={setPlan} testId="subs-filter-plan" options={[["all", t("subs.all")], ...PLANS.map((p) => [p, p === "custom" ? t("subs.custom") : p[0].toUpperCase() + p.slice(1)])]} />
        <Picker label={t("subs.sort")} value={sort} onChange={setSort} testId="subs-sort" options={Object.keys(SORTS).map((k) => [k, t(`subs.sort_${k}`)])} />
      </div>
      {list.length ? (
        <ul className="mt-4 grid min-w-0 gap-3 md:grid-cols-2 xl:grid-cols-3" data-testid="subs-list">{list.map((s) => <SubscriberCard key={s.id} s={s} profit={profit?.clients?.[s.id]} />)}</ul>
      ) : (
        <p className="mt-6 text-sm text-neutral-400" data-testid="subs-empty">{t("subs.empty")}</p>
      )}
    </section>
  );
};

export default function AdminSubscriptions() {
  const [profit, setProfit] = useProfit();
  const { t, locale } = useI18n();
  const d = useSubscriptions();
  return (
    <div data-testid="admin-subscriptions-page">
      <PageHero eyebrow={t("admin.eyebrow")} title={t("subs.title")}>
        <p className="mt-4 max-w-2xl text-sm text-neutral-400">{t("subs.subtitle")}</p>
        {d && (
          <p className="mt-3 inline-flex items-center gap-2 text-xs text-emerald-300" data-testid="subs-live">
            <Radio className="h-4 w-4 animate-pulse" /> {t("subs.live")} · {t("subs.updated", { time: new Date(d.generated_at).toLocaleTimeString(locale) })}
          </p>
        )}
      </PageHero>
      {!d ? (
        <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-[var(--gold)]" /></div>
      ) : (
        <div className="mx-auto grid max-w-7xl gap-6 px-4 pb-16 sm:px-8">
          <SubsSummary d={d} />
          <PayGateCard />
          <MrrCard mrr={d.mrr} />
          <ProfitCard p={profit} onChange={setProfit} />
          <RevenueSources rev={d.revenue} />
          <CartSplitCard />
          <RobotCard />
          <ChangesCard />
          <TrialSettingsCard />
          <MinisiteKeysCard />
          <FxCard />
          <FeesSection etag={d.etag} />
          <FeeModelCard />
          <Subscribers rows={d.subscribers} profit={profit} />
        </div>
      )}
    </div>
  );
}
